import { mastersApi, fieldConfigApi, vendorSymbolMappingApi, companiesApi, brokerMasterApi, schemeWiseBrokersApi, supportingDataApi, clientMasterApi, custodyAccountsApi } from '@/services/api';
import { MasterConfig } from './types';

// Helper to load clients for NRI client selection fields
const getNRIClients = async (params?: any) => {
    return clientMasterApi.getClients(params);
};

export const MASTER_CONFIGS: MasterConfig[] = [
    {
        key: 'countries',
        label: 'Countries',
        api: {
            list: mastersApi.getCountries,
            create: mastersApi.createCountry,
            update: mastersApi.updateCountry,
            delete: mastersApi.deleteCountry,
        },
        fields: [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'code', label: 'Code', type: 'text', required: true },
        ],
        displayFields: ['name', 'code'],
    },
    {
        key: 'categories',
        label: 'Categories',
        api: {
            list: mastersApi.getCategories,
            create: mastersApi.createCategory,
            update: mastersApi.updateCategory,
            delete: mastersApi.deleteCategory,
        },
        fields: [
            { name: 'name', label: 'Name', type: 'text', required: true },
        ],
        displayFields: ['name'],
    },
    {
        key: 'groups',
        label: 'Groups',
        api: {
            list: mastersApi.getGroups,
            create: mastersApi.createGroup,
            update: mastersApi.updateGroup,
            delete: mastersApi.deleteGroup,
        },
        fields: [
            { name: 'group_code', label: 'Group Code', type: 'text', required: true },
            { name: 'group_name', label: 'Group Name', type: 'text', required: true },
        ],
        displayFields: ['group_code', 'group_name'],
    },
    {
        key: 'distributors',
        label: 'Distributors',
        api: {
            list: mastersApi.getDistributors,
            create: mastersApi.createDistributor,
            update: mastersApi.updateDistributor,
            delete: mastersApi.deleteDistributor,
        },
        fields: [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'code', label: 'Code', type: 'text', required: true },
        ],
        displayFields: ['name', 'code'],
    },
    {
        key: 'client-classes',
        label: 'Client Classes',
        api: {
            list: mastersApi.getClientClasses,
            create: mastersApi.createClientClass,
            update: mastersApi.updateClientClass,
            delete: mastersApi.deleteClientClass,
        },
        fields: [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'class_isin', label: 'Class ISIN', type: 'text' },
        ],
        displayFields: ['name', 'class_isin'],
    },
    {
        key: 'field-configs',
        label: 'Client Field Config',
        api: {
            list: mastersApi.getFieldConfigs,
            create: mastersApi.createFieldConfig,
            update: mastersApi.updateFieldConfig,
            delete: mastersApi.deleteFieldConfig,
            bulkUpdate: fieldConfigApi.bulkUpdate,
        },
        fields: [
            {
                name: 'client_type',
                label: 'Client Type',
                type: 'select',
                required: true,
                options: [
                    { id: 'PMS', name: 'PMS' },
                    { id: 'AIF', name: 'AIF' }
                ]
            },
            {
                name: 'field_name',
                label: 'Field Name',
                type: 'select',
                required: true,
                options: [], // To be populated dynamically
                multiple: true
            },
            { name: 'is_required', label: 'Is Required', type: 'checkbox' },
        ],
        displayFields: ['client_type', 'field_name', 'is_required'],
    },
    {
        key: 'vendor-symbol-mappings',
        label: 'Vendor Symbol Mappings',
        api: {
            list: vendorSymbolMappingApi.getMappings,
            create: vendorSymbolMappingApi.createMapping,
            update: vendorSymbolMappingApi.updateMapping,
            delete: vendorSymbolMappingApi.deleteMapping,
        },
        fields: [
            {
                name: 'vendor_code',
                label: 'Vendor',
                type: 'select',
                required: true,
                options: [
                    { id: 'NUVAMA', name: 'NUVAMA Wealth Management' },
                    { id: 'FORMIDIUM', name: 'Formidium Corp' },
                    { id: 'IBKR', name: 'IBKR' },
                    { id: 'SGAF', name: 'SGAF' },
                ]
            },
            { name: 'symbol', label: 'Vendor Symbol', type: 'text', required: true },
            {
                name: 'company',
                label: 'Company',
                type: 'autocomplete',
                required: true,
                optionApi: companiesApi.getCompanies,
                optionLabelKey: 'custom_label_symbol_name',
                optionValueKey: 'id',
                displayKey: 'company_name'
            },
        ],
        displayFields: ['vendor_code', 'symbol', 'company'],
    },
    {
        key: 'brokers',
        label: 'Brokers',
        api: {
            list: brokerMasterApi.list,
            create: brokerMasterApi.create,
            update: brokerMasterApi.update,
            delete: brokerMasterApi.delete,
        },
        fields: [
            { name: 'broker_name', label: 'Broker Name', type: 'text', required: true },
            { name: 'sebi_code', label: 'SEBI Code', type: 'text', required: true },
            { name: 'target_pct', label: 'Target (%)', type: 'number', required: true },
            {
                name: 'target_amt',
                label: 'Target Amount',
                type: 'number',
                required: true,
                displayFormatter: (value: any) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value)),
            },
        ],
        displayFields: ['broker_name', 'sebi_code', 'target_pct', 'target_amt'],
    },
    {
        key: 'custody-accounts',
        label: 'Custody Accounts',
        api: {
            list: custodyAccountsApi.list,
            create: custodyAccountsApi.create,
            update: custodyAccountsApi.update,
            delete: custodyAccountsApi.delete,
        },
        fields: [
            { name: 'custody_code', label: 'Custody Code', type: 'text', required: true },
            {
                name: 'scheme',
                label: 'Scheme',
                type: 'autocomplete',
                required: true,
                optionApi: supportingDataApi.getSchemes,
                optionLabelKey: 'name',
                optionValueKey: 'id',
                displayKey: 'scheme_name'
            },
            // Write-only server side: blank leaves the stored password untouched, so an
            // edit that does not retype it cannot lock the ingest out of the archive.
            { name: 'zip_password', label: 'Zip Password (blank keeps current)', type: 'text' },
            { name: 'client_code_override', label: 'Client Code Override', type: 'text' },
            { name: 'is_api_based', label: 'Fetched From Broker API', type: 'checkbox' },
        ],
        displayFields: ['custody_code', 'scheme_name', 'client_code_override', 'is_api_based'],
    },
    {
        key: 'scheme-wise-brokers',
        label: 'Scheme-Wise Brokers',
        api: {
            list: schemeWiseBrokersApi.list,
            create: schemeWiseBrokersApi.create,
            update: schemeWiseBrokersApi.update,
            delete: schemeWiseBrokersApi.delete,
        },
        fields: [
            { name: 'broker_code', label: 'Broker Code', type: 'text', required: true },
            {
                name: 'broker',
                label: 'Broker',
                type: 'autocomplete',
                required: true,
                optionApi: brokerMasterApi.getBrokers,
                optionLabelKey: 'broker_name',
                optionValueKey: 'id',
                displayKey: 'broker_name'
            },
            {
                name: 'scheme',
                label: 'Scheme',
                type: 'autocomplete',
                required: true,
                optionApi: supportingDataApi.getSchemes,
                optionLabelKey: 'name',
                optionValueKey: 'id',
                displayKey: 'scheme_name'
            },
            {
                name: 'nri_client_master',
                label: 'NRI Client',
                type: 'autocomplete',
                optionApi: getNRIClients,
                optionParams: { is_nri: true },
                optionLabelKey: 'full_name',
                optionValueKey: 'id',
                displayKey: 'nri_client_name'
            },
            { name: 'custody_code', label: 'Custody Code', type: 'text' },
            { name: 'is_default', label: 'Is Default', type: 'checkbox' },
        ],
        displayFields: ['broker_code', 'broker_name', 'scheme_name', 'custody_code', 'is_default'],
    },
];
